import { z } from 'zod';
import { UsdAmount } from './ai.js';
import { FORMAT_MAX_QUESTIONS, FORMAT_SIZE_MAX, FORMAT_SIZE_MIN, FORMAT_TEXT_SAMPLE, FormatQuestion, FormatSettings, IMAGE_ALIGNS, IMAGE_MAX_PCT, IMAGE_MIN_PCT, IMAGE_WRAPS, ImageChange, ImageSummary, MAX_BLOCK_IMAGES, } from './chilldoc-format.js';
/**
 * Dán thông minh trong `.chilldoc` — APM-SP-a, docs/plan-smart-paste.md §5.
 *
 * AI (skill `format_paste`) chỉ đề xuất **kế hoạch định dạng** cho đoạn vừa
 * dán, theo ngữ cảnh quanh chỗ chèn — không viết lại chữ (người dùng chốt
 * 2026-09-28). Client áp tất định (`applyPastePlan`) và người dùng Giữ hoặc
 * Về dạng dán thường — luật #6. Đường làm tay: "Chèn ngay".
 */
/** Nguồn clipboard — một tab của hộp Dán thông minh. */
export const PASTE_SOURCES = ['markdown', 'markdownPreview', 'word', 'excel', 'other'];
export const PASTE_CONTEXT_BEFORE = 12;
export const PASTE_CONTEXT_AFTER = 6;
export const PASTE_OUTLINE_MAX = 40;
export const PASTE_MAX_BLOCKS = 200;
/** Độ sâu lồng tối đa của danh sách (0 = cấp ngoài cùng). */
export const PASTE_MAX_DEPTH = 4;
/** Thụt lề trái tối đa AI được đặt cho một đoạn (mm) — ~4 bước Tab 12,7 mm. */
export const PASTE_MAX_INDENT_MM = 50;
/** Số cột tối đa của một bảng mà AI chia độ rộng (APM-SP-g). */
export const PASTE_MAX_COLUMNS = 30;
/** Một cột chiếm ít/nhiều nhất bao nhiêu % bề ngang bảng. */
export const PASTE_COLUMN_MIN_PCT = 3;
export const PASTE_COLUMN_MAX_PCT = 90;
/**
 * Ảnh (APM-SP-i): từ APM-FMT-a schema ở `chilldoc-format.ts` dùng chung với căn
 * chỉnh tài liệu — tên cũ giữ làm bí danh để chỗ gọi không đổi.
 */
export const PASTE_MAX_IMAGES = MAX_BLOCK_IMAGES;
export const PASTE_IMAGE_MIN_PCT = IMAGE_MIN_PCT;
export const PASTE_IMAGE_MAX_PCT = IMAGE_MAX_PCT;
export const PASTE_IMAGE_WRAPS = IMAGE_WRAPS;
export const PASTE_IMAGE_ALIGNS = IMAGE_ALIGNS;
export const PasteImageSummary = ImageSummary;
export const PasteImageChange = ImageChange;
/**
 * Tóm tắt một cột của bảng dán — APM-SP-g: đủ để chia độ rộng cho hợp với tiêu
 * đề và dữ liệu (dài nhất, trung bình, có phải cột số/ngày không).
 */
export const PasteColumnSummary = z.object({
    header: z.string().max(40),
    maxChars: z.number().int().nonnegative(),
    avgChars: z.number().nonnegative(),
    numeric: z.boolean(),
});
const Size = z.number().int().min(FORMAT_SIZE_MIN).max(FORMAT_SIZE_MAX);
const FontName = z.string().trim().min(1).max(60);
/**
 * Một khối, rút gọn. Danh sách được **trải phẳng**: mỗi mục là một khối
 * `bullet`/`ordered` với `depth` — model đổi được từng dòng.
 */
export const PasteBlockSummary = z.object({
    index: z.number().int().nonnegative(),
    /** `image` = đoạn chỉ gồm ảnh (APM-SP-i). */
    type: z.enum([
        'paragraph',
        'heading',
        'bullet',
        'ordered',
        'quote',
        'code',
        'table',
        'image',
        'other',
    ]),
    level: z.number().int().min(1).max(6).optional(),
    depth: z.number().int().min(0).max(PASTE_MAX_DEPTH).optional(),
    /** Thụt lề trái (mm) của đoạn/tiêu đề. */
    indentLeft: z.number().min(0).max(200).optional(),
    /** ≤ 120 ký tự đầu. */
    text: z.string().max(FORMAT_TEXT_SAMPLE),
    fonts: z.array(z.string().max(60)).max(10),
    sizes: z.array(z.number()).max(10),
    /** Chỉ khối `table`: các cột theo thứ tự. */
    columns: z.array(PasteColumnSummary).max(PASTE_MAX_COLUMNS).optional(),
    /** Ảnh trong khối, theo thứ tự (APM-SP-i). */
    images: z.array(PasteImageSummary).max(PASTE_MAX_IMAGES).optional(),
});
const PastePlanLazy = z.lazy(() => PastePlan);
export const PastePlanInput = z.object({
    /** Khối ngay **trước** chỗ chèn, gần nhất ở cuối. */
    before: z.array(PasteBlockSummary).max(PASTE_CONTEXT_BEFORE),
    /** Khối ngay **sau** chỗ chèn. */
    after: z.array(PasteBlockSummary).max(PASTE_CONTEXT_AFTER),
    /** Tiêu đề của cả tài liệu — biết chỗ chèn đang ở mục nào, cấp mấy. */
    outline: z
        .array(z.object({ level: z.number().int().min(1).max(6), text: z.string().max(FORMAT_TEXT_SAMPLE) }))
        .max(PASTE_OUTLINE_MAX),
    /** Đoạn vừa dán, `index` 0…n-1. */
    pasted: z.array(PasteBlockSummary).min(1).max(PASTE_MAX_BLOCKS),
    source: z.enum(PASTE_SOURCES),
    /** Font Google tài liệu đã thêm — kế hoạch chỉ được chọn trong đây + font hệ thống. */
    fonts: z.array(FontName).max(30),
    /** Bề ngang vùng chữ của trang (px) — AI tính cỡ ảnh để vừa hàng (APM-SP-i). */
    pageWidth: z.number().int().min(200).max(4000).optional(),
    prompt: z.string().trim().max(500).optional(),
    runId: z.uuid().optional(),
    /**
     * APM-FMT-e/f: kế hoạch nháp `analyzePaste` đã tính local + câu hỏi cho chỗ
     * mập mờ. Model chỉ trả lời câu hỏi; không câu hỏi ⇒ server trả nháp, 0 lượt.
     */
    draft: PastePlanLazy,
    questions: z.array(FormatQuestion).max(FORMAT_MAX_QUESTIONS),
    settings: FormatSettings.optional(),
});
/** Đổi một khối của đoạn dán. `keep` = giữ nguyên loại (chỉ nhận font/cỡ chung). */
export const PasteBlockChange = z.object({
    index: z.number().int().nonnegative(),
    as: z.enum(['paragraph', 'heading', 'bullet', 'ordered', 'keep']),
    level: z.number().int().min(1).max(4).optional(),
    depth: z.number().int().min(0).max(PASTE_MAX_DEPTH).optional(),
    indentLeft: z.number().min(0).max(PASTE_MAX_INDENT_MM).optional(),
    /**
     * Chỉ bảng (`as: 'keep'`): % bề ngang của từng cột, đủ số cột, tổng ≈ 100 —
     * client quy ra px theo bề ngang trang (APM-SP-g).
     */
    columnWidths: z
        .array(z.number().min(PASTE_COLUMN_MIN_PCT).max(PASTE_COLUMN_MAX_PCT))
        .min(1)
        .max(PASTE_MAX_COLUMNS)
        .optional(),
    /** Khối có ảnh: cách đặt **từng** ảnh, đủ số ảnh của khối (APM-SP-i). */
    images: z.array(PasteImageChange).min(1).max(PASTE_MAX_IMAGES).optional(),
    /**
     * Chỉ khối `image`: dời khối tới **ngay sau** khối `moveAfter` của đoạn dán (-1 = lên
     * đầu đoạn dán) — đặt ảnh cạnh đoạn nói về nó, hay gom các ảnh liền nhau thành hàng.
     */
    moveAfter: z.number().int().min(-1).max(PASTE_MAX_BLOCKS).optional(),
});
/** `null` ở font/cỡ = không đặt riêng (theo mặc định của tài liệu). */
export const PastePlan = z.object({
    bodyFont: FontName.nullable(),
    bodySize: Size.nullable(),
    headingFont: FontName.nullable(),
    headingSizes: z.object({
        h1: Size.nullable(),
        h2: Size.nullable(),
        h3: Size.nullable(),
        h4: Size.nullable(),
    }),
    blocks: z.array(PasteBlockChange).max(PASTE_MAX_BLOCKS),
    /** ≤ 3 câu tiếng Việt: đã đổi gì, vì sao. */
    notes: z.array(z.string().trim().max(200)).max(3),
});
export const PastePlanResult = z.object({
    plan: PastePlan,
    costUsd: UsdAmount,
    partial: z.boolean(),
    /** Câu trả lời đã lọc (APM-FMT-f) — rỗng khi không có lượt LLM. */
    answers: z
        .array(z.object({ id: z.string(), choice: z.string(), why: z.string().optional() }))
        .max(FORMAT_MAX_QUESTIONS)
        .default([]),
});
// ---------------------------------------------------------------------------
// Ghép câu trả lời vào kế hoạch dán — APM-FMT-e/f
// ---------------------------------------------------------------------------
function upsertPasteBlock(blocks, change) {
    return [...blocks.filter((block) => block.index !== change.index), change].sort((a, b) => a.index - b.index);
}
const clampLevel = (level) => Math.min(4, Math.max(1, level));
/**
 * Ghép câu trả lời vào kế hoạch nháp của đoạn dán — hàm thuần, dùng chung web
 * (xem trước) và API. Trả lời ngoài `options` ⇒ bỏ qua; trả lời bằng `default`
 * vẫn ghép (idempotent, như `mergeAnswers`).
 *
 * - `heading`: `hN` ⇒ khối thành tiêu đề cấp N; `paragraph` ⇒ bỏ thay đổi của khối.
 * - `level`: `sibling` ⇒ mọi tiêu đề của đoạn dán lên một cấp (nháp đã coi là con).
 * - `fontRole`: font thân đổi.
 * - `image`: ảnh `slot` của khối `index` đặt theo `wrap:%:align`.
 * - `imageMove`: `move` ⇒ ảnh đầu của `group` dời ngay sau `target`, ảnh sau nối đuôi;
 *   `stay` ⇒ bỏ `moveAfter` của cả nhóm.
 */
export function mergePasteAnswers(draft, questions, answers) {
    const byId = new Map(answers.map((answer) => [answer.id, answer.choice]));
    let plan = { ...draft, blocks: [...draft.blocks] };
    const changeOf = (index) => plan.blocks.find((block) => block.index === index);
    for (const question of questions) {
        const choice = byId.get(question.id);
        if (choice === undefined || !question.options.includes(choice))
            continue;
        switch (question.kind) {
            case 'heading': {
                if (question.index === undefined)
                    break;
                if (choice === 'paragraph') {
                    plan = { ...plan, blocks: plan.blocks.filter((block) => block.index !== question.index) };
                }
                else {
                    const level = Number(choice.replace(/^h/, ''));
                    if (level >= 1 && level <= 4) {
                        plan = {
                            ...plan,
                            blocks: upsertPasteBlock(plan.blocks, {
                                index: question.index,
                                as: 'heading',
                                level,
                            }),
                        };
                    }
                }
                break;
            }
            case 'level': {
                const shift = choice === 'sibling' ? -1 : 0;
                plan = {
                    ...plan,
                    blocks: plan.blocks.map((block) => block.as === 'heading' && block.level !== undefined
                        ? { ...block, level: clampLevel(block.level + shift) }
                        : block),
                };
                break;
            }
            case 'fontRole':
                plan = {
                    ...plan,
                    bodyFont: choice,
                    headingFont: plan.headingFont === choice ? null : plan.headingFont,
                };
                break;
            case 'image': {
                if (question.index === undefined)
                    break;
                const [wrap, pct, align] = choice.split(':');
                const widthPct = Number.parseInt(pct ?? '', 10);
                if (!wrap || !align)
                    break;
                const existing = changeOf(question.index);
                const images = [...(existing?.images ?? [])];
                const slot = question.slot ?? 0;
                while (images.length <= slot)
                    images.push({ wrap: 'inline', align: 'left' });
                images[slot] = {
                    wrap: wrap,
                    align: align,
                    ...(Number.isFinite(widthPct) ? { widthPct } : {}),
                };
                plan = {
                    ...plan,
                    blocks: upsertPasteBlock(plan.blocks, {
                        ...(existing ?? { index: question.index, as: 'keep' }),
                        images,
                    }),
                };
                break;
            }
            case 'imageMove': {
                const group = question.group ?? (question.index !== undefined ? [question.index] : []);
                if (group.length === 0 || question.target === undefined)
                    break;
                let previous = question.target;
                for (const index of group) {
                    const existing = changeOf(index) ?? { index, as: 'keep' };
                    const { moveAfter: _drop, ...rest } = existing;
                    const next = choice === 'move' ? { ...rest, moveAfter: previous } : rest;
                    plan = { ...plan, blocks: upsertPasteBlock(plan.blocks, next) };
                    previous = index;
                }
                break;
            }
            default:
                break;
        }
    }
    return plan;
}
//# sourceMappingURL=chilldoc-paste.js.map